import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from '@/i18n/I18nContext';
import { LanguageSelector } from '@/components/LanguageSelector';
import { AlertFeedPanel } from '@/components/AlertToast';
import NotificationStatus from '@/components/NotificationStatus';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { CheckCircle, XCircle, AlertCircle, MessageSquare, Settings as SettingsIcon, Zap, ArrowRight, Inbox as InboxIcon, Calendar as CalendarIcon, Bug, Mail } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, tenant, logout } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [whatsappConfig, setWhatsappConfig] = useState(null);
  const [settings, setSettings] = useState(null);
  const [evaEnabled, setEvaEnabled] = useState(true);
  const [evaPausedUntil, setEvaPausedUntil] = useState(null);
  const [forceLanguageEnabled, setForceLanguageEnabled] = useState(false);
  const [forceLanguageValue, setForceLanguageValue] = useState('it');
  const [verificationSending, setVerificationSending] = useState(false);

  const fetchDashboardData = useCallback(async () => {
    try {
      const [messagesRes, whatsappRes, settingsRes] = await Promise.all([
        api.get('/messages?limit=10'), api.get('/whatsapp/config'), api.get('/tenant/settings'),
      ]);
      setMessages(messagesRes.data.messages || []);
      setWhatsappConfig(whatsappRes.data);
      setSettings(settingsRes.data);
      setEvaEnabled(settingsRes.data?.eva_enabled ?? true);
      setEvaPausedUntil(settingsRes.data?.eva_paused_until);
      setForceLanguageEnabled(settingsRes.data?.force_language_enabled ?? false);
      setForceLanguageValue(settingsRes.data?.force_language_value ?? 'it');
    } catch (error) { console.error('Failed to fetch dashboard data:', error); toast.error(t('dashboard.dataLoadError')); }
    finally { setLoading(false); }
  }, [t]);

  const handleStripeReturn = useCallback(async (sessionId) => {
    let activated = false;
    try {
      const res = await api.get(`/billing/session-status?session_id=${encodeURIComponent(sessionId)}`);
      if (res.data.subscription_status === 'active') {
        activated = true;
        toast.success(t('dashboard.subscriptionActivated') + ' ✅');
        // Force a clean auth-context reload so the wizard sees the newly active tenant immediately.
        window.location.replace('/wizard');
        return;
      }
      toast.info(t('dashboard.checkoutReceived'));
    } catch (error) {
      console.error('Error checking session:', error);
    } finally {
      if (!activated) {
        window.history.replaceState({}, '', '/dashboard');
        fetchDashboardData();
      }
    }
  }, [fetchDashboardData, t]);

  const handleResendVerification = async () => {
    setVerificationSending(true);
    try {
      await api.post('/auth/verify-email/send');
      toast.success(t('verifyEmail.sent'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('verifyEmail.sendError'));
    } finally {
      setVerificationSending(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    if (sessionId) handleStripeReturn(sessionId);
  }, [fetchDashboardData, handleStripeReturn]);

  const handleEvaToggle = async () => {
    try {
      const newStatus = !evaEnabled;
      await api.patch('/tenant/eva-status', { eva_enabled: newStatus });
      setEvaEnabled(newStatus);
      if (newStatus) { setEvaPausedUntil(null); toast.success(t('dashboard.evaReactivated')); }
      else toast.success(t('dashboard.evaPaused'));
    } catch (error) { toast.error(t('dashboard.statusUpdateError')); }
  };

  const handlePause = async (minutes) => {
    try {
      await api.patch('/tenant/eva-status', { pause_minutes: minutes });
      const pauseUntil = new Date(Date.now() + minutes * 60 * 1000).toISOString();
      setEvaPausedUntil(pauseUntil); setEvaEnabled(true);
      toast.success(t('dashboard.pausedFor', { minutes }));
    } catch (error) { toast.error(t('dashboard.pauseError')); }
  };

  const getEvaStatus = () => {
    if (!evaEnabled) return { active: false, text: t('dashboard.evaPaused'), variant: 'error' };
    if (evaPausedUntil) { const pd = new Date(evaPausedUntil); if (pd > new Date()) return { active: false, text: t('dashboard.evaPaused'), variant: 'error' }; }
    return { active: true, text: t('dashboard.evaActive'), variant: 'success' };
  };

  const handleLanguageControlSave = async () => {
    try {
      await api.put('/tenant/settings', { force_language_enabled: forceLanguageEnabled, force_language_value: forceLanguageValue });
      toast.success(t('dashboard.languageSaved')); fetchDashboardData();
    } catch (error) { toast.error(t('dashboard.languageSaveError')); }
  };

  const handleContactLanguageChange = async (phone, language) => {
    try {
      await api.patch('/contacts/by-phone/language', { phone, language_override: language === 'auto' ? null : language });
      toast.success(t('dashboard.contactLanguageUpdated')); fetchDashboardData();
    } catch (error) { toast.error(t('dashboard.contactLanguageError')); }
  };

  const getSubscriptionBadge = () => {
    if (tenant?.plan === 'internal') return (<Badge variant="info" className="flex items-center gap-2" data-testid="badge-subscription-internal"><CheckCircle className="w-4 h-4" />Internal (Founder)</Badge>);
    const status = tenant?.subscription_status;
    const variants = {
      active: { variant: 'success', icon: CheckCircle, text: t('dashboard.active') },
      past_due: { variant: 'warning', icon: AlertCircle, text: t('dashboard.pastDue') },
      canceled: { variant: 'error', icon: XCircle, text: t('dashboard.canceled') },
      incomplete: { variant: 'default', icon: AlertCircle, text: t('dashboard.incomplete') },
    };
    const config = variants[status] || variants.incomplete;
    const Icon = config.icon;
    return (<Badge variant={config.variant} className="flex items-center gap-2" data-testid={`badge-subscription-${status}`}><Icon className="w-4 h-4" />{config.text}</Badge>);
  };

  const getWhatsAppBadge = () => {
    if (!whatsappConfig || !whatsappConfig.configured) return (<Badge variant="default" className="flex items-center gap-2" data-testid="badge-whatsapp-disconnected"><XCircle className="w-4 h-4" />{t('dashboard.notConfigured')}</Badge>);
    return (<Badge variant="success" className="flex items-center gap-2" data-testid="badge-whatsapp-connected"><CheckCircle className="w-4 h-4" />{t('dashboard.connected')}</Badge>);
  };

  const needsSetup = () => {
    if (tenant?.plan === 'internal') return !whatsappConfig?.configured;
    return tenant?.subscription_status !== 'active' || !whatsappConfig?.configured;
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50">
      <DashboardHeader user={user} logout={logout} />
      <div className="max-w-7xl mx-auto px-6 py-8"><div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8"><Skeleton className="h-32" /><Skeleton className="h-32" /><Skeleton className="h-32" /></div><Skeleton className="h-64" /></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50" data-testid="dashboard-page">
      <DashboardHeader user={user} logout={logout} />
      <div className="max-w-7xl mx-auto px-6 py-8">
        {user && !user.email_verified && (
          <Alert className="mb-8 border-amber-200 bg-amber-50" data-testid="alert-email-verification">
            <Mail className="h-4 w-4 text-amber-700" />
            <AlertTitle className="text-amber-900">{t('verifyEmail.bannerTitle')}</AlertTitle>
            <AlertDescription className="text-amber-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <span>{t('verifyEmail.bannerDesc')}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleResendVerification}
                disabled={verificationSending}
                data-testid="button-resend-verification"
              >
                {verificationSending ? t('verifyEmail.sending') : t('verifyEmail.resend')}
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {needsSetup() && (
          <Alert className="mb-8 border-emerald-200 bg-emerald-50" data-testid="alert-setup-required">
            <AlertCircle className="h-4 w-4 text-emerald-600" />
            <AlertTitle className="text-emerald-900">{t('dashboard.completeSetup')}</AlertTitle>
            <AlertDescription className="text-emerald-800">
              {tenant?.plan !== 'internal' && tenant?.subscription_status !== 'active' && <span>{t('dashboard.activateSubscription')} </span>}
              {!whatsappConfig?.configured && <span>{t('dashboard.configureWhatsApp')} </span>}
              <Button variant="ghost" size="sm" className="ml-2 text-emerald-700 hover:text-emerald-900" onClick={() => navigate('/wizard')} data-testid="button-start-setup">
                {t('dashboard.startSetup')} <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="transition-card" data-testid="card-subscription">
            <CardHeader><div className="flex items-center justify-between"><CardTitle className="text-lg">{t('dashboard.subscription')}</CardTitle>{getSubscriptionBadge()}</div></CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600 mb-4">{tenant?.plan === 'internal' ? t('dashboard.founderPlan') : tenant?.subscription_status === 'active' ? t('dashboard.monthlyActive') : t('dashboard.setupRequired')}</p>
              {tenant?.plan !== 'internal' && tenant?.subscription_status !== 'active' && <Button size="sm" onClick={() => navigate('/wizard')} data-testid="button-activate-subscription">{t('dashboard.activateBtn')}</Button>}
            </CardContent>
          </Card>

          <Card className="transition-card" data-testid="card-whatsapp">
            <CardHeader><div className="flex items-center justify-between"><CardTitle className="text-lg">{t('dashboard.whatsapp')}</CardTitle>{getWhatsAppBadge()}</div></CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600 mb-4">{whatsappConfig?.configured ? <>{t('dashboard.phoneNumber')}: {whatsappConfig.phone_number || t('dashboard.notSpecified')}</> : t('dashboard.notConfigured')}</p>
              <Button size="sm" variant="outline" onClick={() => navigate(whatsappConfig?.configured ? '/settings' : '/wizard')} data-testid="button-configure-whatsapp">{whatsappConfig?.configured ? t('common.edit') : t('dashboard.configure')}</Button>
            </CardContent>
          </Card>

          <Card className="transition-card" data-testid="card-messages">
            <CardHeader><CardTitle className="text-lg">{t('dashboard.messages')}</CardTitle></CardHeader>
            <CardContent><div className="text-3xl font-bold text-slate-900 mb-2">{messages.length}</div><p className="text-sm text-slate-600">{t('dashboard.lastMessages')}</p></CardContent>
          </Card>

          <Card className="transition-card" data-testid="card-eva-status">
            <CardHeader><div className="flex items-center justify-between"><CardTitle className="text-lg">{t('dashboard.evaStatus')}</CardTitle><Badge variant={getEvaStatus().variant} data-testid="badge-eva-status">{getEvaStatus().active ? '🟢' : '🔴'} {getEvaStatus().text}</Badge></div></CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">{t('dashboard.autoResponses')}</span>
                  <button onClick={handleEvaToggle} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${evaEnabled ? 'bg-emerald-600' : 'bg-gray-300'}`} data-testid="toggle-eva"><span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${evaEnabled ? 'translate-x-6' : 'translate-x-1'}`} /></button>
                </div>
                {evaEnabled && (
                  <div className="pt-2 border-t border-gray-200">
                    <p className="text-xs text-slate-500 mb-2">{t('dashboard.quickPause')}:</p>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => handlePause(30)} className="text-xs" data-testid="button-pause-30">30min</Button>
                      <Button size="sm" variant="outline" onClick={() => handlePause(60)} className="text-xs" data-testid="button-pause-60">1h</Button>
                      <Button size="sm" variant="outline" onClick={() => handlePause(120)} className="text-xs" data-testid="button-pause-120">2h</Button>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="mb-8" data-testid="card-language-control">
          <CardHeader><CardTitle>{t('dashboard.languageControl')}</CardTitle><CardDescription>{t('dashboard.languageControlDesc')}</CardDescription></CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="default-language" className="text-sm font-medium text-slate-900">{t('dashboard.defaultLanguage')}</Label>
                <select id="default-language" value={settings?.default_language || 'it'} onChange={(e) => setSettings({ ...settings, default_language: e.target.value })} className="flex h-10 w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm" data-testid="select-default-language">
                  <option value="it">🇮🇹 {t('languages.it')}</option><option value="de">🇩🇪 {t('languages.de')}</option><option value="en">🇬🇧 {t('languages.en')}</option><option value="es">🇪🇸 {t('languages.es')}</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="force-language-value" className="text-sm font-medium text-slate-900">{t('dashboard.forceLanguage')}</Label>
                <div className="flex items-center gap-3">
                  <button onClick={() => setForceLanguageEnabled(!forceLanguageEnabled)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${forceLanguageEnabled ? 'bg-emerald-600' : 'bg-gray-300'}`} data-testid="toggle-force-language"><span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${forceLanguageEnabled ? 'translate-x-6' : 'translate-x-1'}`} /></button>
                  <select id="force-language-value" value={forceLanguageValue} onChange={(e) => setForceLanguageValue(e.target.value)} disabled={!forceLanguageEnabled} className="flex h-10 w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm disabled:opacity-50" data-testid="select-force-language">
                    <option value="it">🇮🇹 {t('languages.it')}</option><option value="de">🇩🇪 {t('languages.de')}</option><option value="en">🇬🇧 {t('languages.en')}</option><option value="es">🇪🇸 {t('languages.es')}</option>
                  </select>
                </div>
                <p className="text-xs text-slate-500">{forceLanguageEnabled ? t('dashboard.forceLanguageOn') : t('dashboard.forceLanguageOff')}</p>
              </div>
            </div>
            <Button onClick={handleLanguageControlSave} data-testid="button-save-language">{t('dashboard.saveLanguage')}</Button>
          </CardContent>
        </Card>

        <Card className="mb-8" data-testid="card-quick-actions">
          <CardHeader><CardTitle>{t('dashboard.quickActions')}</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              <Button variant="outline" onClick={() => navigate('/inbox')} data-testid="button-inbox"><InboxIcon className="w-4 h-4 mr-2" />{t('dashboard.openInbox')}</Button>
              <Button variant="outline" onClick={() => navigate('/calendar')} data-testid="button-calendar"><CalendarIcon className="w-4 h-4 mr-2" />{t('dashboard.calendar')}</Button>
              <Button variant="outline" onClick={() => navigate('/wizard')} data-testid="button-wizard"><Zap className="w-4 h-4 mr-2" />{t('dashboard.setupWizard')}</Button>
              <Button variant="outline" onClick={() => navigate('/settings')} data-testid="button-settings"><SettingsIcon className="w-4 h-4 mr-2" />{t('common.settings')}</Button>
              <Button variant="outline" onClick={fetchDashboardData} data-testid="button-refresh">{t('dashboard.refreshData')}</Button>
            </div>
          </CardContent>
        </Card>

        {/* Alert Feed */}
        <div className="mb-8" data-testid="dashboard-alert-feed">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-600">{t('alerts.recentAlerts') || 'Notifiche'}</h3>
            <NotificationStatus />
          </div>
          <AlertFeedPanel maxItems={15} />
        </div>

        <Card data-testid="card-recent-messages">
          <CardHeader><CardTitle>{t('dashboard.recentMessages')}</CardTitle><CardDescription>{t('dashboard.recentMessagesDesc')}</CardDescription></CardHeader>
          <CardContent>
            {messages.length === 0 ? (
              <div className="text-center py-12" data-testid="empty-messages">
                <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">{t('dashboard.noMessages')}</p>
                <p className="text-sm text-slate-400 mt-2">{t('dashboard.noMessagesHint')}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="table-messages">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">{t('dashboard.contact')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">{t('dashboard.direction')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">{t('dashboard.message')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">{t('dashboard.language')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">{t('dashboard.date')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {messages.map((msg, idx) => {
                      const contactPhone = msg.contact_id?.split('_')[1] || 'Unknown';
                      return (
                        <tr key={msg.id} className="hover:bg-gray-50" data-testid={`message-row-${idx}`}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">{contactPhone}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm"><Badge variant={msg.direction === 'in' ? 'default' : 'info'}>{msg.direction === 'in' ? t('dashboard.received') : t('dashboard.sent')}</Badge></td>
                          <td className="px-6 py-4 text-sm text-slate-600 max-w-md truncate">{msg.has_media ? '[Media]' : msg.body}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <select value={msg.language_override || 'auto'} onChange={(e) => handleContactLanguageChange(contactPhone, e.target.value)} className="text-xs rounded border border-gray-300 px-2 py-1" data-testid={`select-lang-${idx}`} disabled={msg.direction === 'out'}>
                              <option value="auto">{t('languages.auto')}</option><option value="it">🇮🇹 IT</option><option value="de">🇩🇪 DE</option><option value="en">🇬🇧 EN</option><option value="es">🇪🇸 ES</option>
                            </select>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">{formatTimestamp(msg.created_at)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function DashboardHeader({ user, logout }) {
  const navigate = useNavigate();
  const { t, lang } = useTranslation();

  const supportContent = {
    it: { label: 'Supporto', subject: 'Segnalazione bug EVA', body: 'Descrivi qui il problema.\n\nPer bug di messaggistica, allega gli screenshot della chat e spiega:\n1. Cosa ha scritto il cliente\n2. Cosa ha risposto EVA\n3. Cosa avrebbe dovuto rispondere\n4. Data e ora circa' },
    de: { label: 'Support', subject: 'EVA Fehlermeldung', body: 'Beschreibe hier das Problem.\n\nBei Nachrichtenfehlern füge bitte Screenshots des Chats bei und erkläre:\n1. Was der Kunde geschrieben hat\n2. Was EVA geantwortet hat\n3. Was EVA hätte antworten sollen\n4. Ungefähres Datum und Uhrzeit' },
    en: { label: 'Support', subject: 'EVA Bug Report', body: 'Describe the problem here.\n\nFor messaging bugs, please attach chat screenshots and explain:\n1. What the customer wrote\n2. What EVA replied\n3. What EVA should have replied\n4. Approximate date and time' },
    es: { label: 'Soporte', subject: 'Reporte de error EVA', body: 'Describe aquí el problema.\n\nPara errores de mensajería, adjunta capturas de pantalla del chat y explica:\n1. Qué escribió el cliente\n2. Qué respondió EVA\n3. Qué debería haber respondido EVA\n4. Fecha y hora aproximadas' },
  };
  const sc = supportContent[lang] || supportContent.en;

  return (
    <header className="border-b border-gray-200 bg-white" data-testid="dashboard-header">
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <MessageSquare className="w-8 h-8 text-emerald-600" />
            <span className="text-2xl font-bold text-slate-900">EVA AI</span>
          </div>
          <div className="flex items-center space-x-4">
            <LanguageSelector />
            <span className="text-sm text-slate-600 hidden sm:inline">{user?.email}</span>
            <Button variant="ghost" size="sm" onClick={() => navigate('/account')} data-testid="button-nav-account">{t('common.profile')}</Button>
            <Button variant="ghost" size="sm" onClick={() => navigate('/settings')} data-testid="button-nav-settings"><SettingsIcon className="w-4 h-4" /></Button>
            {user?.role === 'founder' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/founder')}
                data-testid="button-founder-console"
                className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
              >
                Founder Console
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              asChild
              data-testid="button-support"
            >
              <a href={`mailto:support@eva-ai-assistant.com?subject=${encodeURIComponent(sc.subject)}&body=${encodeURIComponent(sc.body)}`}>
                <Bug className="w-4 h-4 mr-1" />
                {sc.label}
              </a>
            </Button>
            <Button variant="outline" size="sm" onClick={logout} data-testid="button-logout">{t('common.logout')}</Button>
          </div>
        </div>
      </div>
    </header>
  );
}
